import { useCrossFlow } from '@atlassiansox/cross-flow-support/trello';
import { useCallback, useMemo } from 'react';

import type { OperationalEventSchema } from '@trello/analytics-event-schemas';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import { useCrossFlowForAllUsersExperience } from './experimental/useCrossFlowForAllUsersExperience';
import {
  useCrossFlowForDeFactoBandits,
  type CrossFlowForDefactoBanditsExperimentVariations,
} from './experimental/useCrossFlowForDeFactoBandits';
import { useCrossFlowForFreeExistingUsersExperience } from './experimental/useCrossFlowForFreeExistingUsersExperience';
import { useCrossFlowForJiraWorkItemCreation } from './experimental/useCrossFlowForJiraWorkItemCreation';
import { useCrossFlowForNewUserExperience } from './experimental/useCrossFlowForNewUserExperience';
import { useCrossFlowForPaidUsersExperience } from './experimental/useCrossFlowForPaidUsersExperience';
import { useCrossFlowWorkspaces } from './experimental/useCrossFlowWorkspaces';
import type { TouchpointSourceType } from './TouchpointSourceType';
import {
  trelloCrossFlowOpen,
  type TrelloCrossFlow,
  type TrelloCrossFlowOptions,
} from './TrelloCrossFlow';

type CrossFlowDeFactoBanditsEnrolledOperationalEventSchema =
  OperationalEventSchema<{
    action: 'enrolled';
    actionSubject: 'crossFlowExperiment';
    source: TouchpointSourceType;
    attributes: {
      experiment: {
        type: 'crossFlowForDeFactoBandits';
        description: 'De facto bandits cross-flow experiment key';
      };
    };
    _meta: {
      id: 61980;
      owner: 'trello-web-eng';
      description: 'Cross-flow de facto bandits experiment enrollment';
    };
  }>;

type CrossFlowExperienceServedOperationalEventSchema = OperationalEventSchema<{
  action: 'served';
  actionSubject: 'crossFlowExperience';
  source: TouchpointSourceType;
  attributes: {
    experience: {
      type: string;
      description: 'Experience identifier served for this touchpoint';
    };
    cohortType: {
      type: 'dynamic';
      description: 'Cohort assignment mode for the served experience';
    };
  };
  _meta: {
    id: 72495;
    owner: 'trello-web-eng';
    description: 'Cross-flow experience variant served for the touchpoint';
  };
}>;

/**
 * Union type of all possible variations for crossFlow experiments
 */
export type ExperimentVariations =
  TrelloCrossFlow<CrossFlowForDefactoBanditsExperimentVariations>;

export const useTrelloCrossFlow = (
  source: TouchpointSourceType,
  {
    workspaceId,
    boardId,
    isNewTrelloUser,
    jiraTemplateId,
    shouldSkipMarketingPage,
    interactionName,
  }: TrelloCrossFlowOptions = {},
): ExperimentVariations => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();

  // Resolve the member's eligible workspaces once and thread them through every
  // experience hook below. Each experience previously resolved entitlements and
  // the provisioning query independently, producing many redundant Apollo
  // subscriptions to the same data — a measured performance bottleneck.
  const workspaces = useCrossFlowWorkspaces();

  const analyticsContainers = useMemo(
    () => ({
      organization: {
        id: workspaceId,
      },
      board: {
        id: boardId,
      },
    }),
    [workspaceId, boardId],
  );

  const { value: doSendOperationalEvent } = useFeatureGate(
    'trello_xf_experiment_analytics_toggle',
  );

  const sendOperationalEvent = useCallback(
    (operationalEventFn: () => void) => {
      if (doSendOperationalEvent) {
        operationalEventFn();
      }
    },
    [doSendOperationalEvent],
  );

  const crossFlowForDeFactoBandits = useCrossFlowForDeFactoBandits({
    source,
    workspaceId,
    hasValidAaSession,
    workspaces,
  });

  const crossFlowForJiraWorkItemCreation = useCrossFlowForJiraWorkItemCreation({
    source,
    workspaceId,
    boardId,
    hasValidAaSession,
    workspaces,
  });

  /**
   * Hooks for productionized experiences
   *
   */
  const crossFlowForNewUserExperience = useCrossFlowForNewUserExperience({
    source,
    workspaceId,
    boardId,
    hasValidAaSession,
    jiraTemplateId,
    workspaces,
  });

  const defaultPaidUsersCrossFlowExperience =
    useCrossFlowForPaidUsersExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
      jiraTemplateId,
      workspaces,
    });

  const defaultFreeExistingUsersCrossFlowExperience =
    useCrossFlowForFreeExistingUsersExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
      jiraTemplateId,
      workspaces,
    });

  const defaultAllUsersCrossFlowExperience = useCrossFlowForAllUsersExperience({
    source,
    workspaceId,
    boardId,
    hasValidAaSession,
    jiraTemplateId,
    workspaces,
  });

  const trelloCrossFlow = {
    open: trelloCrossFlowOpen(crossFlow, source, [], hasValidAaSession, {
      workspaceId,
      boardId,
      isNewTrelloUser,
      shouldSkipMarketingPage,
      interactionName,
    }),
    getIsTargeted: (): boolean =>
      crossFlowForDeFactoBandits.getIsTargeted() ||
      crossFlowForJiraWorkItemCreation.getIsTargeted() ||
      defaultPaidUsersCrossFlowExperience.getIsTargeted() ||
      defaultFreeExistingUsersCrossFlowExperience.getIsTargeted() ||
      crossFlowForNewUserExperience.getIsTargeted() ||
      defaultAllUsersCrossFlowExperience.getIsTargeted(),
    getExperimentCohort: (): null => null,
    isEligible: false,
  };

  const { value: isDiscoveryAdExperienceEnabled } = useFeatureGate(
    'trello_xf_discovery_ads_control',
  );

  // Return early if crossFlow is disabled (missing context provider, incompatible version...)
  if (!crossFlow.isEnabled || !isDiscoveryAdExperienceEnabled) {
    return trelloCrossFlow;
  }

  // Check if the current source is eligible for the de facto bandits experiment
  if (crossFlowForDeFactoBandits.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowDeFactoBanditsEnrolledOperationalEventSchema>(
        {
          action: 'enrolled',
          actionSubject: 'crossFlowExperiment',
          source,
          containers: analyticsContainers,
          attributes: {
            experiment: 'crossFlowForDeFactoBandits',
          },
        },
      );
    });
    return crossFlowForDeFactoBandits;
  }

  if (crossFlowForJiraWorkItemCreation.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowExperienceServedOperationalEventSchema>(
        {
          action: 'served',
          actionSubject: 'crossFlowExperience',
          source,
          containers: analyticsContainers,
          attributes: {
            experience: crossFlowForJiraWorkItemCreation.experience,
            cohortType: 'dynamic',
          },
        },
      );
    });

    return {
      ...crossFlowForJiraWorkItemCreation,
      getExperimentCohort: () => null,
    } satisfies ExperimentVariations;
  }

  if (defaultPaidUsersCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowExperienceServedOperationalEventSchema>(
        {
          action: 'served',
          actionSubject: 'crossFlowExperience',
          source,
          containers: analyticsContainers,
          attributes: {
            experience: defaultPaidUsersCrossFlowExperience.experience,
            cohortType: 'dynamic',
          },
        },
      );
    });

    return {
      ...defaultPaidUsersCrossFlowExperience,
      getExperimentCohort: () => null,
    } satisfies ExperimentVariations;
  }

  if (defaultFreeExistingUsersCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowExperienceServedOperationalEventSchema>(
        {
          action: 'served',
          actionSubject: 'crossFlowExperience',
          source,
          containers: analyticsContainers,
          attributes: {
            experience: defaultFreeExistingUsersCrossFlowExperience.experience,
            cohortType: 'dynamic',
          },
        },
      );
    });

    return {
      ...defaultFreeExistingUsersCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }

  if (crossFlowForNewUserExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowExperienceServedOperationalEventSchema>(
        {
          action: 'served',
          actionSubject: 'crossFlowExperience',
          source,
          containers: analyticsContainers,
          attributes: {
            experience: crossFlowForNewUserExperience.experience,
            cohortType: 'dynamic',
          },
        },
      );
    });
    return {
      ...crossFlowForNewUserExperience,
      getExperimentCohort: () => null,
    };
  }

  if (defaultAllUsersCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent<CrossFlowExperienceServedOperationalEventSchema>(
        {
          action: 'served',
          actionSubject: 'crossFlowExperience',
          source,
          containers: analyticsContainers,
          attributes: {
            experience: defaultAllUsersCrossFlowExperience.experience,
            cohortType: 'dynamic',
          },
        },
      );
    });

    return {
      ...defaultAllUsersCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }
  return trelloCrossFlow;
};
